import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CaretRight,
  CheckCircle,
  Info,
  Warning,
  WarningOctagon,
  X,
} from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { classifyAlertSeverity } from '../../utils/dashboardAlerts';
import { dismissAlertId, readDismissedAlertIds } from '../../utils/dismissedAlerts';
import { formatGuidedFlowTemplate } from '../../utils/guidedFlowUtils';
import { formatAlertCopy } from './budgetAlertCardUtils';

/** `ds/alerts/item--*` no .pen — só danger (alertas de sistema) não é dismissable. */
const TONE = {
  danger: {
    Icon: WarningOctagon,
    wrap: 'border-danger-500 bg-danger-tint',
    text: 'text-danger-fg',
    dismissable: false,
  },
  warning: {
    Icon: Warning,
    wrap: 'border-warning-500 bg-warning-tint',
    text: 'text-warning-fg',
    dismissable: true,
  },
  info: {
    Icon: Info,
    wrap: 'border-info-500 bg-info-tint',
    text: 'text-info-fg',
    dismissable: true,
  },
  success: {
    Icon: CheckCircle,
    wrap: 'border-success-500 bg-success-tint',
    text: 'text-success-fg',
    dismissable: true,
  },
};

const VISIBLE_LIMIT = 4;

/** category_budget_* têm `title` = só o nome da categoria — junta o badge de estado. */
function alertDisplayTitle(alert, copy) {
  if (alert.type?.startsWith('category_budget_') && copy.badge) {
    return `${copy.title} · ${copy.badge}`;
  }
  return copy.title;
}

function AlertItem({ alert, onDismiss }) {
  const tone = TONE[classifyAlertSeverity(alert)] || TONE.info;
  const { Icon } = tone;
  const copy = formatAlertCopy(alert);
  const title = alertDisplayTitle(alert, copy);

  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-space-3 rounded-ds-lg border p-space-3',
        tone.wrap,
      )}
    >
      <Link to={alert.href} className="flex min-w-0 flex-1 items-start gap-space-3">
        {/* eslint-disable-next-line no-restricted-syntax -- dimensão de ícone sem token exacto */}
        <Icon className={cn('h-[18px] w-[18px] shrink-0', tone.text)} aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className={cn('truncate text-type-body-sm font-semibold', tone.text)}>{title}</p>
          {copy.message ? (
            <p className="text-type-label text-muted-foreground">{copy.message}</p>
          ) : null}
        </div>
        {/* eslint-disable-next-line no-restricted-syntax -- dimensão de ícone sem token exacto */}
        <CaretRight className="mt-0.5 h-[14px] w-[14px] shrink-0 text-muted-foreground" aria-hidden />
      </Link>
      {tone.dismissable ? (
        <button
          type="button"
          onClick={onDismiss}
          className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={pt.alerts.dismiss}
        >
          {/* eslint-disable-next-line no-restricted-syntax -- dimensão de ícone sem token exacto */}
          <X className="h-[14px] w-[14px]" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/**
 * `ds/alerts/panel` no .pen — secção própria (nunca embutida num hero), sempre
 * visível quando há alertas, sem accordion. Dashboard mostra o conjunto
 * completo de `buildDashboardAlerts`; Despesas passa só os relacionados com
 * orçamento/categoria (sem contexto de FE → sem passar `efApiAlerts`).
 */
export function AlertsPanel({ alerts, className, onCustomDismiss }) {
  const [dismissed, setDismissed] = useState(() => readDismissedAlertIds());
  const [expanded, setExpanded] = useState(false);

  const visible = (alerts || []).filter((alert) => !dismissed.has(alert.id));
  if (visible.length === 0) return null;

  const shown = expanded ? visible : visible.slice(0, VISIBLE_LIMIT);
  const remaining = visible.length - shown.length;

  return (
    <section className={cn('flex w-full min-w-0 flex-col gap-space-3', className)}>
      <div className="flex w-full min-w-0 items-center gap-space-2 text-muted-foreground">
        <span className="type-section">{pt.dashboard.alerts.sectionTitle}</span>
        <span className="type-chip min-w-0 truncate font-normal">
          {formatGuidedFlowTemplate(pt.dashboard.alerts.hint, { count: visible.length })}
        </span>
      </div>
      <div className="flex flex-col gap-space-2">
        {shown.map((alert) => (
          <AlertItem
            key={alert.id}
            alert={alert}
            onDismiss={async () => {
              if (onCustomDismiss) {
                await onCustomDismiss(alert);
              } else {
                dismissAlertId(alert.id);
              }
              setDismissed((prev) => {
                const next = new Set(prev);
                next.add(alert.id);
                return next;
              });
            }}
          />
        ))}
      </div>
      {remaining > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center justify-center gap-space-1 pt-space-1 text-type-label font-semibold text-sage-on-tint"
        >
          {formatGuidedFlowTemplate(pt.dashboard.alerts.more, { count: remaining })}
          <CaretRight className="h-3 w-3" aria-hidden />
        </button>
      ) : null}
    </section>
  );
}

export default AlertsPanel;
